import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import ImageViewer from '../components/shared/ImageViewer';
import VideoPlayer from '../components/shared/VideoPlayer';
import LandingNav from '../components/shared/LandingNav';
import StickyCTA from '../components/shared/StickyCTA';
import SiteFooter from '../components/shared/SiteFooter';
import analyticsService from '../services/analyticsService';
import { useScrollTracking } from '../hooks/useScrollTracking';
import { BROKER_INTEGRATION_OPTIONS } from '../constants/brokerIntegrationOptions';
import Seo from '../components/Seo';
import LandingBackdrop from '../components/landing/LandingBackdrop';
import {
  BracketTargetsIcon,
  LiquidityDepthIcon,
  PositionSizeIcon,
} from '../components/landing/LandingAppendixIcons';
import {
  ChevronDown,
  Cpu,
  JournalText,
  ListCheck,
  Phone,
  ShieldCheck,
} from 'react-bootstrap-icons';
import { FEATURES_PAGE_SECTION_LINKS, featuresSectionId } from '../data/featuresPageNav';
import './Landing.css';
import './HomeDesk.css';
import './Features.css';
import { restoreLandingAccentOverride } from '../utils/landingAccent';

const FEATURE_NAV_ICONS = {
  '#how-it-works': ListCheck,
  '#trading-rules': ShieldCheck,
  '#bracket-builder': BracketTargetsIcon,
  '#auto-position-sizing': PositionSizeIcon,
  '#trade-journal': JournalText,
  '#liquidity-levels': LiquidityDepthIcon,
  '#mobile-trading': Phone,
  '#precision-ai': Cpu,
};

const FEATURE_NAV_LINKS = FEATURES_PAGE_SECTION_LINKS.map((link) => ({
  ...link,
  id: featuresSectionId(link.hash),
  Icon: FEATURE_NAV_ICONS[link.hash],
}));

const FEATURE_GUIDE_IDS = new Set(FEATURE_NAV_LINKS.map((link) => link.id));

const HOW_IT_WORKS_STEPS = [
  {
    number: '1',
    title: 'Before the Trade',
    summary: 'Set the limits you want enforced.',
    paragraphs: [
      'Set risk parameters, trading hours, and the other limits you want enforced.',
      'When a limit such as daily max loss is hit, new positions are blocked. You can still close or reduce an open position.',
    ],
    hashes: ['#trading-rules'],
  },
  {
    number: '2',
    title: 'Entering the Trade',
    summary: 'Plan the exit, then size the trade from your risk.',
    paragraphs: [
      'Plan the exit before you enter: a stop, or a bracket with more than one take-profit.',
      'Set the risk for the trade and the position size updates as you move the stop. Sync a limit entry to the latest price, or place a market, stop, or stop-limit order.',
    ],
    hashes: ['#bracket-builder', '#auto-position-sizing'],
  },
  {
    number: '3',
    title: 'During the Trade',
    summary: 'Change orders on the chart and watch liquidity.',
    paragraphs: [
      'Drag orders on the chart to change them. Turn on the Level 2 liquidity overlay to see where large orders are resting.',
      'Move the stop to break even in one click. Risk updates in real time, and alerts fire if a limit is reached.',
    ],
    hashes: ['#liquidity-levels'],
  },
  {
    number: '4',
    title: 'After the Trade',
    summary: 'A journal opens with the trade filled in.',
    paragraphs: [
      'Closing a position opens a journal with the trade details already filled in. Use a template or your own fields.',
      'The health dashboard shows how risk management is going.',
    ],
    hashes: ['#trade-journal'],
  },
];

const FEATURE_CONTENT = {
  'trading-rules': {
    subtitle: 'You choose Track or Enforce for each rule.',
    modes: [
      {
        id: 'track',
        title: 'Track',
        text: 'Flags violations without blocking—so you can see the pattern first.',
      },
      {
        id: 'enforce',
        title: 'Enforce',
        text: 'Hard limits: orders that break your selected rules are blocked.',
      },
    ],
    bullets: [
      'Max daily loss — auto-lockout when you hit your threshold',
      'Max trades per day — cap how many positions you can open per day',
      'Max risk per trade — standardize risk across all trades',
      'Require a stop loss — ensures every trade has defined risk before placement',
      'Allowed trading hours — set the hours you can open new trades (for example, 9:30 AM–11:00 AM EST). Outside those hours, new trades are blocked. You can still manage open positions.',
      'Manual lockout — set a timer when you are tilted. No override.',
      { rule: 'Target Daily Profit', detail: 'locks out new trades once you hit your daily profit target' },
      { rule: 'Consecutive loss cooldown', detail: 'blocks new trades after several losses in a row' },
    ],
    video: {
      src: '/videos/enable-risk-rule.mp4',
      alt: 'Demo of enabling a risk rule',
    },
  },
  'bracket-builder': {
    subtitle: 'Stop guessing your exits mid-trade.',
    description: [
      {
        lead: 'Multiple targets and scale-out',
        body: 'Set more than one take-profit, with a different quantity at each. For example, 25% at target 1, 50% at target 2, and 25% at target 3. Define the exit once.',
      },
      {
        lead: 'One-click brackets',
        body: 'Use a template (stop only, one target, or several targets) or build your own. Drag the levels on the chart and submit once. Entry, stop, and targets go out together.',
      },
      {
        lead: 'Risk while you build',
        body: 'Change risk per trade while you build the bracket. Position size updates as it does when you drag the stop, so a new setup stays quick.',
      },
    ],
    video: {
      src: '/videos/bracket-builder-demo.mp4',
      poster: '/images/bracket-config.png',
      alt: 'Bracket builder with multiple take-profits',
    },
  },
  'auto-position-sizing': {
    subtitle: 'Stop doing math while the market moves.',
    description: 'Set risk per trade in dollars or percent. Dragging the stop-loss on the chart recalculates position size.\n\nNo spreadsheet and no mental math.',
    video: {
      src: '/videos/changing-risk-per-trade.mp4',
      alt: 'Demo of changing risk per trade',
    },
  },
  'trade-journal': {
    subtitle: 'You can\'t improve what you don\'t track.',
    description: 'A journal prompt opens when you close a position, with the trade details filled in. Use a built-in template or your own fields: setup, execution, emotions, what worked, what did not.',
    image: '/images/trade-journal-entry-2.png',
    imageAlt: 'Trade journal entry with the trade details filled in',
  },
  'liquidity-levels': {
    subtitle: 'See where liquidity sits.',
    description: 'The liquidity indicator draws Level 2 data as a heatmap so clusters of large orders are easy to see on the chart. Use it to spot possible stop runs and support or resistance.\n\nRequires a TradeStation Level 2 data subscription.',
    image: '/images/liquidity-levels.png',
    imageAlt: 'Chart with the liquidity overlay showing order clusters',
  },
  'mobile-trading': {
    subtitle: 'The same rules, on your phone.',
    description: 'Manage positions, place bracket orders, and monitor your account on the go. Risk limits and Enforce mode still blocks new trades that break your trading rules.',
    image: '/images/mobile-chart-trading.png',
    imageAlt: 'PrecisionTrader chart and order entry on a phone',
  },
  'precision-ai': {
    subtitle: 'Patterns in your journals and trades.',
    description: 'PrecisionAI uses journals, trades and account information to spot trading mistakes. Discover patterns in your trading behavior, ask about your open positions, setup new trades, research and more!',
    note: 'For education only. Not investment advice.',
    image: '/images/product_showcase/mobile-ai-chat.png',
    imageAlt: 'PrecisionAI chat',
  },
};

function featureDescriptionBlocks(description) {
  if (!description) return [];
  if (Array.isArray(description)) return description;
  return description
    .split('\n')
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

const Features = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const featuresPageRef = useRef(null);
  const [imageViewer, setImageViewer] = useState({ isOpen: false, src: '', alt: '' });
  const [feedbackForm, setFeedbackForm] = useState({
    type: '', // 'feature' or 'broker'
    broker: '',
    otherBrokerName: '',
    featureRequest: '',
    email: user?.email || ''
  });
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [openHowStep, setOpenHowStep] = useState(null);

  const hashId = featuresSectionId(location.hash);
  const activeFeature = FEATURE_NAV_LINKS.find((link) => link.id === hashId) || FEATURE_NAV_LINKS[0];
  const activeContent = FEATURE_CONTENT[activeFeature.id];
  const isHowItWorks = activeFeature.id === 'how-it-works';
  const playActiveVideo = FEATURE_GUIDE_IDS.has(hashId) && !reduceMotion;

  // Track scroll depth on features page
  useScrollTracking();

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const id = featuresSectionId(location.hash);
    let frame = 0;
    let inner = 0;
    frame = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (!el) return;
        const motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ behavior: motion ? 'auto' : 'smooth', block: 'start' });
        const heading = document.getElementById(`${id}-title`);
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
        if (FEATURE_GUIDE_IDS.has(id)) {
          const nav = document.querySelector('.features-guide-index');
          const link = nav?.querySelector('.is-current');
          if (nav && link && nav.scrollWidth > nav.clientWidth) {
            const left = link.getBoundingClientRect().left
              - nav.getBoundingClientRect().left
              + nav.scrollLeft
              - 12;
            nav.scrollTo({ left, behavior: motion ? 'auto' : 'smooth' });
          }
        }
      });
    });
    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(inner);
    };
  }, [location.pathname, location.hash]);

  useEffect(() => {
    restoreLandingAccentOverride(featuresPageRef.current);
  }, []);

  const openImageViewer = (src, alt) => {
    setImageViewer({ isOpen: true, src, alt });
  };

  const closeImageViewer = () => {
    setImageViewer({ isOpen: false, src: '', alt: '' });
  };

  const handleFeedbackChange = (e) => {
    const { name, value } = e.target;
    setFeedbackForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'broker' && value !== 'Other' ? { otherBrokerName: '' } : {}),
    }));
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    
    const resolvedBroker =
      feedbackForm.broker === 'Other'
        ? feedbackForm.otherBrokerName.trim()
        : feedbackForm.broker.trim();

    // Validate based on type
    if (feedbackForm.type === 'broker' && !resolvedBroker) return;
    if (feedbackForm.type === 'feature' && !feedbackForm.featureRequest.trim()) return;
    if (!feedbackForm.type) return;
    
    setIsSubmittingFeedback(true);
    setFeedbackStatus(null);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const emailProvided = feedbackForm.email && feedbackForm.email.trim() !== '';
      const emailToUse = emailProvided ? feedbackForm.email : (user?.email || 'feedback@precisiontrader.com');
      
      let subject, message;
      
      if (feedbackForm.type === 'broker') {
        subject = `Broker Integration Request: ${resolvedBroker}`;
        message = `Broker Request: ${resolvedBroker}`;
        if (feedbackForm.broker === 'Other') {
          message += '\n(Selected: Other)';
        }
      } else {
        subject = 'Feature Request / Feedback';
        message = `Feature Request/Feedback:\n${feedbackForm.featureRequest}`;
      }
      
      if (!emailProvided && !user?.email) {
        message += '\n\n(No email provided - user requested to remain anonymous)';
      }

      const response = await fetch(`${BACKEND_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token || ''}`
        },
        body: JSON.stringify({
          email: emailToUse,
          subject,
          message,
          userId: user?.id || null,
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        setFeedbackStatus('success');
        setFeedbackForm({
          type: '',
          broker: '',
          otherBrokerName: '',
          featureRequest: '',
          email: user?.email || ''
        });
        setTimeout(() => setFeedbackStatus(null), 5000);
      } else {
        setFeedbackStatus('error');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      setFeedbackStatus('error');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <div ref={featuresPageRef} className="features-page landing-page landing-template home-desk">
      <Seo
        title="Features — Rules, Journals & Analytics | PrecisionTrader"
        description="Explore PrecisionTrader features: configurable trading rules, automatic trade journals, bracket orders, liquidity overlay, PrecisionAI, and analytics."
        canonicalPath="/features"
      />
      <LandingBackdrop />
      <div className="landing-content-shell">
      {/* Navigation */}
      <LandingNav />

      {/* Hero Section */}
      <section className="landing-hero features-hero-section features-hero">
        <div className="features-hero-inner">
          <span className="landing-hero-category features-hero-kicker">Feature guide</span>
          <h1 className="features-hero-title">
            Everything you need to know about{' '}
            <span className="features-hero-title-accent">PrecisionTrader</span>
          </h1>
          <p className="features-hero-subtitle">
            Open one feature at a time. Each name matches the Features menu.
          </p>
          <nav className="features-hero-pills" aria-label="Jump to features">
            {FEATURE_NAV_LINKS.map(({ hash, label, Icon }) => {
              const current = location.hash === hash;
              return (
                <Link
                  key={hash}
                  to={`/features${hash}`}
                  className={`features-hero-pill${current ? ' is-current' : ''}`}
                  aria-current={current ? 'true' : undefined}
                >
                  <Icon aria-hidden="true" />
                  <span>{label}</span>
                </Link>
              );
            })}
          </nav>
          <div className="features-hero-cta-wrap">
            <Link to="/register" className="landing-cta-btn">
              Enforce Your Rules
            </Link>
          </div>
        </div>
      </section>

      <section className="features-section-block features-section-block--padded features-guide-section" aria-label="Key features">
        <div className="features-section-inner">
          <div className="features-guide">
            <nav className="features-guide-index" aria-label="Key features">
              {FEATURE_NAV_LINKS.map(({ hash, id, label, Icon }) => {
                const current = FEATURE_GUIDE_IDS.has(hashId)
                  ? id === hashId
                  : !hashId && id === activeFeature.id;
                return (
                  <Link
                    key={hash}
                    to={`/features${hash}`}
                    className={`features-guide-index-link${current ? ' is-current' : ''}`}
                    aria-current={current ? 'true' : undefined}
                  >
                    <Icon aria-hidden="true" />
                    <span>{label}</span>
                  </Link>
                );
              })}
            </nav>

            <article
              id={activeFeature.id}
              className="features-guide-detail"
              aria-labelledby={`${activeFeature.id}-title`}
            >
              <h2 id={`${activeFeature.id}-title`} className="features-guide-title">
                {activeFeature.label}
              </h2>
              {isHowItWorks ? (
                <p className="features-guide-subtitle">
                  Before the trade, at entry, while it is open, and after you close.
                </p>
              ) : activeContent.subtitle && (
                <p className="features-guide-subtitle">{activeContent.subtitle}</p>
              )}
              <div key={activeFeature.id} className="features-guide-stage">
              {isHowItWorks ? (
                <div className="features-guide-media">
                  <button
                    type="button"
                    className="features-guide-shot"
                    onClick={() => openImageViewer(
                      '/images/product_showcase/bracket-order-chart.png',
                      'PrecisionTrader chart with bracket targets, stop loss, and the order ticket'
                    )}
                    aria-label="Enlarge: PrecisionTrader chart with bracket targets, stop loss, and the order ticket"
                  >
                    <img
                      src="/images/product_showcase/bracket-order-chart.png"
                      alt=""
                    />
                  </button>
                </div>
              ) : activeContent.video ? (
                <div className="features-guide-media">
                  <VideoPlayer
                    key={activeContent.video.src}
                    src={activeContent.video.src}
                    poster={activeContent.video.poster}
                    alt={activeContent.video.alt}
                    title={activeFeature.label}
                    inline
                    muted
                    loop
                    controls
                    autoplay={playActiveVideo}
                  />
                </div>
              ) : activeContent.image ? (
                <div className="features-guide-media">
                  <button
                    type="button"
                    className="features-guide-shot"
                    onClick={() => openImageViewer(activeContent.image, activeContent.imageAlt)}
                    aria-label={`Enlarge: ${activeContent.imageAlt}`}
                  >
                    <img src={activeContent.image} alt="" />
                  </button>
                </div>
              ) : null}
              <div className="features-guide-copy">
                {isHowItWorks && (
                  <ol className="features-steps">
                    {HOW_IT_WORKS_STEPS.map((step) => {
                      const open = openHowStep === step.number;
                      const panelId = `how-step-${step.number}`;
                      return (
                        <li key={step.number} className={`features-step${open ? ' is-open' : ''}`}>
                          <h3 className="features-step-heading">
                            <button
                              type="button"
                              className="features-step-toggle"
                              aria-expanded={open}
                              aria-controls={panelId}
                              onClick={() => setOpenHowStep(open ? null : step.number)}
                            >
                              <span className="features-step-index" aria-hidden="true">{step.number}</span>
                              <span className="features-step-toggle-text">
                                <span className="features-step-title">{step.title}</span>
                                <span className="features-step-summary">{step.summary}</span>
                              </span>
                              <ChevronDown aria-hidden="true" className="features-step-chevron" />
                            </button>
                          </h3>
                          {open && (
                            <div id={panelId} className="features-step-panel">
                              {step.paragraphs.map((paragraph) => (
                                <p key={paragraph} className="features-step-copy">{paragraph}</p>
                              ))}
                              <p className="features-step-links">
                                {step.hashes.map((hash) => {
                                  const link = FEATURE_NAV_LINKS.find((item) => item.hash === hash);
                                  if (!link) return null;
                                  return (
                                    <Link key={hash} to={`/features${hash}`}>
                                      {link.label}
                                    </Link>
                                  );
                                })}
                              </p>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
                {featureDescriptionBlocks(activeContent?.description).map((block, idx) => (
                  block && typeof block === 'object' ? (
                    <p key={idx}>
                      <strong className="features-guide-lead">{block.lead}</strong>
                      {' — '}
                      {block.body}
                    </p>
                  ) : (
                    <p key={idx}>{block}</p>
                  )
                ))}
                {activeContent?.modes && (
                  <ul className="features-guide-modes" aria-label="Rule modes">
                    {activeContent.modes.map((mode) => (
                      <li key={mode.id} className={`features-guide-modes__item features-guide-modes__item--${mode.id}`}>
                        <span className="features-guide-modes__title">{mode.title}</span>
                        <p className="features-guide-modes__text">{mode.text}</p>
                      </li>
                    ))}
                  </ul>
                )}
                {activeContent?.bullets && (
                  <ul className="features-guide-list">
                    {activeContent.bullets.map((bullet, idx) => {
                      if (bullet && typeof bullet === 'object' && typeof bullet.rule === 'string') {
                        return (
                          <li key={idx}>
                            <span className="features-guide-rule">{bullet.rule}</span>
                            {bullet.detail ? ` — ${bullet.detail}` : ''}
                          </li>
                        );
                      }
                      const text = typeof bullet === 'string' ? bullet : String(bullet ?? '');
                      const parts = text.split(' — ');
                      return (
                        <li key={idx}>
                          {parts.length > 1 ? (
                            <>
                              <span className="features-guide-rule">{parts[0]}</span>
                              {` — ${parts.slice(1).join(' — ')}`}
                            </>
                          ) : text}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {activeContent?.note && (
                  <p className="features-guide-note">{activeContent.note}</p>
                )}
              </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Why PrecisionTrader Section */}
      <section className="features-section-block features-section-block--padded">
        <div className="features-section-inner">
        <h2 className="features-section-title">
          Why PrecisionTrader?
        </h2>
        <div className="why-precisiontrader-grid">
          <div className="features-comparison-card">
            <h3 className="features-comparison-card__title">
              vs. Native Broker Platforms
            </h3>
            <p className="features-comparison-card__body">
              Most broker platforms focus on order execution, not rule enforcement. PrecisionTrader <strong>adds the discipline layer that's missing</strong>.
            </p>
            <ul className="features-comparison-card__list">
              <li>Automatic position sizing based on risk</li>
              <li>Enforced daily loss limits</li>
              <li>Trading hour restrictions</li>
              <li>Automated trade journaling</li>
            </ul>
          </div>
          <div className="features-comparison-card">
            <h3 className="features-comparison-card__title">
              vs. Journaling Apps
            </h3>
            <p className="features-comparison-card__body">
              Journaling apps help you reflect after the fact. PrecisionTrader helps you <strong>follow your rules in real-time</strong>, before mistakes happen.
            </p>
            <ul className="features-comparison-card__list">
              <li>Prevents rule violations instead of just tracking them</li>
              <li>Pre-filled journal prompts (trades auto-populated, quick review)</li>
              <li>Integrated with your actual trading account</li>
              <li>Real-time risk monitoring</li>
            </ul>
          </div>
          <div className="features-comparison-card">
            <h3 className="features-comparison-card__title">
              vs. Sticky Notes & Checklists
            </h3>
            <p className="features-comparison-card__body">
              You can write your rules down, but <strong>nothing's enforcing them</strong>. PrecisionTrader automatically blocks trades that violate your limits, even when emotions take over.
            </p>
            <ul className="features-comparison-card__list">
              <li>Automatic enforcement, not just reminders</li>
              <li>Blocks trades outside your trading hours</li>
              <li>Prevents exceeding daily loss limits</li>
              <li>Enforces position sizing rules</li>
            </ul>
          </div>
          <div className="features-comparison-card">
            <h3 className="features-comparison-card__title">
              vs. Youtube Gurus
            </h3>
            <p className="features-comparison-card__body">
              Instead of just following someone else's signals, PrecisionTrader helps you <strong>define your own strategy, rules, and risk</strong>.
            </p>
            <ul className="features-comparison-card__list">
              <li>Build your own trading plan</li>
              <li>Set rules that match your strategy</li>
              <li>Define risk parameters that work for you</li>
              <li>Enforce your plan, not someone else's</li>
            </ul>
          </div>
        </div>
        </div>
      </section>

      <section id="platform-walkthrough" className="features-section-block features-section-block--padded">
        <div className="features-section-inner">
          <h2 id="platform-walkthrough-title" className="features-section-title">
            See the System in Action
          </h2>
          <p className="features-section-lede features-section-lede--center">
            One walkthrough of the whole platform. Press play when you want it.
          </p>
          <div className="features-demo-video">
            <VideoPlayer
              src="/videos/precision-trader-overview.mp4"
              poster="/images/precision-trader-overview-photo.png"
              alt="Platform walkthrough of bracket orders, position sizing, risk alerts, and the journal"
              title="See the System in Action"
              controls
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="features-section-block features-section-block--padded features-cta-block">
        <div className="features-section-inner">
          <h2 className="features-section-title">
            Ready to Trade With Structure?
          </h2>
          <p className="features-cta-lede">
            Start trading with structure and enforce your rules today.
          </p>
          <div className="features-cta-actions">
            <Link
              to="/register"
              className="landing-cta-btn landing-cta-btn-large"
              onClick={() => {
                let landingVersion = 'unknown';
                try {
                  landingVersion = sessionStorage.getItem('landing_page_version') || 'unknown';
                } catch (e) {}

                analyticsService.trackTradingEvent('cta_button_clicked', {
                  variant: 'features-page',
                  user_id: user?.id || null,
                  is_logged_in: !!user,
                  landing_page_version: landingVersion
                });
              }}
            >
              Enforce Your Rules
            </Link>
          </div>
        </div>
      </section>

      {/* Feedback Section */}
      <section className="features-section-block features-section-block--padded features-feedback-block">
        <div className="features-section-inner">
          <h2 className="features-section-title features-section-title--compact">
            Have feedback or a feature request?
          </h2>
          
          <form onSubmit={handleFeedbackSubmit} className="features-feedback-form">
            <div className="features-feedback-type-row">
              <label className="features-feedback-type-label">
                <input
                  type="radio"
                  name="type"
                  value="feature"
                  checked={feedbackForm.type === 'feature'}
                  onChange={handleFeedbackChange}
                />
                Feature request
              </label>
              <label className="features-feedback-type-label">
                <input
                  type="radio"
                  name="type"
                  value="broker"
                  checked={feedbackForm.type === 'broker'}
                  onChange={handleFeedbackChange}
                />
                Broker support
              </label>
            </div>

            {feedbackForm.type === 'broker' && (
              <>
                <select
                  name="broker"
                  value={feedbackForm.broker}
                  onChange={handleFeedbackChange}
                  className="features-feedback-input"
                  required
                >
                  <option value="">Select broker...</option>
                  {BROKER_INTEGRATION_OPTIONS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                {feedbackForm.broker === 'Other' && (
                  <input
                    type="text"
                    name="otherBrokerName"
                    value={feedbackForm.otherBrokerName}
                    onChange={handleFeedbackChange}
                    placeholder="Broker name (required)"
                    className="features-feedback-input"
                    required
                    autoComplete="organization"
                  />
                )}
              </>
            )}

            {feedbackForm.type === 'feature' && (
              <textarea
                name="featureRequest"
                value={feedbackForm.featureRequest}
                onChange={handleFeedbackChange}
                placeholder="I like it, but if it had..."
                rows="3"
                className="features-feedback-input features-feedback-input--textarea"
              />
            )}

            {!user && (
              <input
                type="email"
                name="email"
                value={feedbackForm.email}
                onChange={handleFeedbackChange}
                placeholder="Email (optional)"
                className="features-feedback-input"
              />
            )}

            {feedbackStatus === 'success' && (
              <div className="features-feedback-status features-feedback-status--success">
                Thank you!
              </div>
            )}
            {feedbackStatus === 'error' && (
              <div className="features-feedback-status features-feedback-status--error">
                Error. Try again.
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmittingFeedback || !feedbackForm.type ||
                (feedbackForm.type === 'broker' && !(
                  feedbackForm.broker === 'Other'
                    ? feedbackForm.otherBrokerName.trim()
                    : feedbackForm.broker
                )) ||
                (feedbackForm.type === 'feature' && !feedbackForm.featureRequest.trim())}
              className="features-feedback-submit"
            >
              {isSubmittingFeedback ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter containerClass="features-container" showRiskDisclaimer={true} />
      </div>

      {/* Sticky CTA */}
      <StickyCTA />

      {/* Image Viewer Modal */}
      <ImageViewer
        isOpen={imageViewer.isOpen}
        onClose={closeImageViewer}
        imageSrc={imageViewer.src}
        imageAlt={imageViewer.alt}
      />
    </div>
  );
};

export default Features;

